import type { ReactNode } from 'react';
import { t } from '@lingui/core/macro';
import { BookmarkRowSkeleton, DraggableBookmarkRow } from './Bookmark/Row';
import { Bookmark, ViewMode } from '../types/api';
import {
  BookmarkEditContextProvider,
  useBookmarkEdit,
} from '../contexts/BookmarkEditContext';
import BookmarkEditModal from './BookmarkActions/BookmarkEditModal';

export interface BookmarkListProps {
  bookmarks: Bookmark[];
  condensedView?: boolean;
  showThumbnails?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  emptyMessage?: string;
  mode?: ViewMode;
  profileMode?: boolean;
  /** Optional node rendered inside `.bookmarks`, just below the header divider
   *  and above the rows (e.g. the public-folder indicator on a profile). */
  header?: ReactNode;
}

function BookmarkListContent({
  bookmarks,
  condensedView = false,
  showThumbnails = true,
  isLoading = false,
  isError = false,
  onRetry,
  emptyMessage,
  mode = 'unread',
  profileMode = false,
  header,
}: BookmarkListProps) {
  const { editingBookmark, isEditModalOpen, closeEditModal } = useBookmarkEdit();
  // Handle empty state message based on mode
  let emptyStateMessage = emptyMessage;
  if (!emptyStateMessage) {
    switch (mode) {
      case 'folder':
        emptyStateMessage = t`No articles in this folder.`;
        break;
      case 'liked':
        emptyStateMessage = t`You have no Liked items.`;
        break;
      case 'archive':
        emptyStateMessage = t`You have no Archived items.`;
        break;
      case 'videos':
        emptyStateMessage = t`You have no saved videos.`;
        break;
      default:
        emptyStateMessage = t`No articles saved.`;
    }
  }

  // Map ViewMode to the viewMode prop expected by BookmarkRow
  const getBookmarkViewMode = (mode: ViewMode) => {
    if (mode === 'unread') return 'home';
    if (mode === 'liked') return 'liked';
    if (mode === 'archive' || mode === 'folder') return mode;
    return 'home';
  };

  const bookmarkViewMode = getBookmarkViewMode(mode);

  const bookmarksClassName = 'bookmarks';

  return (
    <>
      {isLoading ? (
        <section className={bookmarksClassName} aria-label={t`Loading articles`}>
          <div className="bookmark-list-header-divider" role="presentation" />
          {Array.from({ length: 8 }).map((_, i) => (
            <BookmarkRowSkeleton
              key={i}
              condensedView={condensedView}
              showThumbnails={showThumbnails}
            />
          ))}
        </section>
      ) : isError ? (
        <div
          className="text-copy-secondary flex flex-col items-center gap-3 py-10 text-center"
          role="alert"
        >
          <p>{t`Sorry, there was an error. Please try again.`}</p>
          {onRetry && (
            <button type="button" className="button-secondary" onClick={onRetry}>
              {t`Try again`}
            </button>
          )}
        </div>
      ) : (
        <>
          <section className={bookmarksClassName} aria-label={t`Articles`}>
            <div className="bookmark-list-header-divider" role="presentation" />
            {header}
            {bookmarks.map((bookmark) => (
              <DraggableBookmarkRow
                key={bookmark.id}
                {...bookmark}
                condensedView={condensedView}
                showThumbnails={showThumbnails}
                viewMode={bookmarkViewMode}
                profileMode={profileMode}
              />
            ))}
          </section>

          {bookmarks.length === 0 && (
            <div className="text-copy-secondary py-10 text-center">
              {emptyStateMessage}
            </div>
          )}
        </>
      )}

      {/* Singleton Edit Modal */}
      {editingBookmark && (
        <BookmarkEditModal
          bookmark={editingBookmark}
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
        />
      )}
    </>
  );
}

function BookmarkList(props: BookmarkListProps) {
  return (
    <BookmarkEditContextProvider>
      <BookmarkListContent {...props} />
    </BookmarkEditContextProvider>
  );
}

export default BookmarkList;
